/**
 * MoonPay frames bridge (no SDK) — HEADLESS path only.
 *
 * Implements MoonPay's documented postMessage v2 protocol directly, so we render
 * the hosted frames ourselves (iframe + postMessage) instead of the @moonpay SDK:
 *   check-connection (headless) → auth (visible OTP login) → buy (headless).
 * We own the KYC data-capture UI (Customer API); MoonPay only owns the
 * compliance-sensitive frames (OTP login, card entry, 3DS). There is NO hosted
 * onboarding here — non-headless countries use the signed widget instead
 * (MoonpayHostedDeposit), and the two never cross over.
 *
 * All frames live on platform.moonpay.com (verified: blocks.moonpay.com 302-
 * redirects these paths, which breaks the iframe handshake).
 *
 * Docs: dev.moonpay.com/platform/frames/{check,auth,buy}
 *       dev.moonpay.com/platform/guides/manual-integration/web
 */

import { type ClientCredentials, decryptClientCredentials, generateKeyPair } from './crypto';

const FRAME_ORIGIN = 'https://platform.moonpay.com';
// Messages may arrive from platform.moonpay.com (and challenge frames from other
// MoonPay subdomains). Accept any *.moonpay.com origin.
const MOONPAY_ORIGIN_RE = /^https:\/\/([a-z0-9-]+\.)*moonpay\.com$/i;
const CHECK_URL = `${FRAME_ORIGIN}/platform/v1/check-connection`;
const BUY_URL = `${FRAME_ORIGIN}/platform/v1/buy`;
// Auth frame = LOGIN ONLY (OTP). This is the Customer-API onboarding path:
// authenticate here, then render our OWN KYC screens.
const AUTH_URL = `${FRAME_ORIGIN}/v2/auth`;

const HEADLESS_STYLE = 'position:absolute;width:0;height:0;border:none;overflow:hidden;';
const VISIBLE_STYLE = 'width:100%;height:100%;min-height:520px;border:none;';
const FRAME_TIMEOUT_MS = 5 * 60 * 1000;
// If the frame never sends its handshake within this window, it almost always
// means the app origin isn't registered with MoonPay (frame stays silent).
const HANDSHAKE_TIMEOUT_MS = 25 * 1000;

interface FrameMessage {
  version: number;
  meta: { channelId: string };
  kind: string;
  payload?: any;
}

function channelIdFor(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ch_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

interface RunFrameOptions {
  url: string;
  params: Record<string, string>;
  container?: HTMLElement | null;
  visible?: boolean;
  /** Called on a `challenge` event; return keeps the frame alive for the later `complete`. */
  onChallenge?: (challengeUrl: string) => void;
}

/** Mount a frame, run the handshake, resolve on `complete` (or `challenge` if unhandled). */
function runFrame(opts: RunFrameOptions): Promise<{ kind: string; payload: any }> {
  return new Promise((resolve, reject) => {
    const channelId = channelIdFor();
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allow', 'payment; camera; microphone; clipboard-write');
    iframe.style.cssText = opts.visible ? VISIBLE_STYLE : HEADLESS_STYLE;
    const qs = new URLSearchParams({ ...opts.params, channelId }).toString();
    const src = `${opts.url}?${qs}`;
    iframe.src = src;

    let settled = false;
    let gotHandshake = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(handshakeTimer);
      clearTimeout(timer);
      iframe.remove();
    };
    const done = (fn: (v: any) => void, value: any) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const ack = (event: MessageEvent) => {
      const msg = JSON.stringify({ version: 2, meta: { channelId }, kind: 'ack' });
      // Reply to the exact sender/origin that sent the handshake.
      try {
        (event.source as WindowProxy | null)?.postMessage(msg, event.origin);
      } catch {
        try {
          iframe.contentWindow?.postMessage(msg, event.origin);
        } catch {
          iframe.contentWindow?.postMessage(msg, FRAME_ORIGIN);
        }
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (!MOONPAY_ORIGIN_RE.test(event.origin)) return;
      let data: FrameMessage;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!data || !data.kind) return;
      // Diagnostic: surface every message MoonPay's frame sends us (any origin).
      console.debug(
        '[moonpay:frame]',
        event.origin,
        data.kind,
        '· channel',
        data.meta?.channelId,
        '· want',
        channelId,
      );
      if (data.meta?.channelId && data.meta.channelId !== channelId) return;

      switch (data.kind) {
        case 'handshake':
          gotHandshake = true;
          clearTimeout(handshakeTimer);
          ack(event);
          break;
        case 'ready':
          break;
        case 'complete':
          done(resolve, { kind: 'complete', payload: data.payload });
          break;
        case 'challenge':
          if (opts.onChallenge && data.payload?.url) {
            opts.onChallenge(data.payload.url); // keep listening for the follow-up `complete`
          } else {
            done(resolve, { kind: 'challenge', payload: data.payload });
          }
          break;
        case 'error':
          done(reject, new Error(data.payload?.message || 'MoonPay frame error'));
          break;
        case 'close':
          done(reject, new Error('MoonPay window was closed'));
          break;
      }
    };

    // Attach the listener BEFORE the iframe loads so we never miss the handshake.
    window.addEventListener('message', onMessage);
    iframe.addEventListener('error', () => {
      console.debug('[moonpay:frame] iframe failed to load', src);
    });
    (opts.container || document.body).appendChild(iframe);

    const handshakeTimer = setTimeout(() => {
      if (!gotHandshake) {
        done(
          reject,
          new Error(
            'MoonPay did not respond. The app domain likely needs to be registered with MoonPay ' +
              '(Developers → Domains / app origin). Check the browser console for details.',
          ),
        );
      }
    }, HANDSHAKE_TIMEOUT_MS);
    const timer = setTimeout(
      () => done(reject, new Error('MoonPay frame timed out')),
      FRAME_TIMEOUT_MS,
    );
  });
}

export interface ConnectResult {
  status: string; // active | connectionRequired | termsAcceptanceRequired | pending | unavailable | failed
  credentials?: ClientCredentials;
  customerId?: string;
}

/** Thrown when the check frame reports the customer must (re)accept MoonPay's terms. */
export class MoonpayTermsRequiredError extends Error {
  code = 'termsAcceptanceRequired' as const;
  constructor() {
    super('MoonPay terms acceptance is required before continuing.');
    this.name = 'MoonpayTermsRequiredError';
  }
}

/**
 * Headless connect: check-connection → (auth if needed), returning active
 * credentials + the MoonPay customer id. No hosted onboarding fallback.
 * @param sessionToken from POST /moonpay/session
 * @param container    element hosting the visible OTP login frame
 */
export async function connectCustomer(
  sessionToken: string,
  container: HTMLElement,
): Promise<ConnectResult> {
  const kp1 = generateKeyPair();
  // `skipKyc` opts the check out of KYC-based statuses so an unverified customer
  // returns `connectionRequired` (we own KYC via the Customer API afterwards).
  const check = await runFrame({
    url: CHECK_URL,
    params: { sessionToken, publicKey: kp1.publicKeyHex, skipKyc: 'true' },
    visible: false,
  });
  const cp = check.payload || {};
  const checkCreds = cp.credentials
    ? decryptClientCredentials(cp.credentials, kp1.privateKeyHex)
    : undefined;

  if (cp.status === 'active') {
    return { status: 'active', credentials: checkCreds, customerId: cp.customer?.id };
  }

  if (cp.status === 'termsAcceptanceRequired') {
    throw new MoonpayTermsRequiredError();
  }

  if (cp.status === 'connectionRequired') {
    const clientToken = checkCreds?.clientToken;
    if (!clientToken) throw new Error('MoonPay did not return a clientToken for auth');
    const kp2 = generateKeyPair();
    // Auth frame = OTP login only. Our KYC screens run afterwards.
    const frame = await runFrame({
      url: AUTH_URL,
      params: { clientToken, publicKey: kp2.publicKeyHex },
      container,
      visible: true,
    });
    const dp = frame.payload || {};
    const activeCreds = dp.credentials
      ? decryptClientCredentials(dp.credentials, kp2.privateKeyHex)
      : undefined;
    return { status: dp.status, credentials: activeCreds, customerId: dp.customer?.id };
  }

  // pending / unavailable / failed
  return { status: cp.status || 'failed', credentials: checkCreds, customerId: cp.customer?.id };
}

export interface BuyResult {
  id: string;
  status: string; // complete | pending | failed
}

/**
 * Run the (headless) buy frame with a quote signature. On a `challenge` (3DS),
 * mounts the challenge frame into `challengeContainer` and keeps waiting for
 * the follow-up `complete`.
 */
export async function runBuyFrame(
  clientToken: string,
  signature: string,
  opts: {
    container?: HTMLElement | null;
    challengeContainer?: HTMLElement | null;
    externalTransactionId?: string;
  },
): Promise<BuyResult> {
  const res = await runFrame({
    url: BUY_URL,
    params: {
      clientToken,
      signature,
      ...(opts.externalTransactionId ? { externalTransactionId: opts.externalTransactionId } : {}),
    },
    container: opts.container,
    visible: false,
    onChallenge: (challengeUrl) => {
      const host = opts.challengeContainer || document.body;
      const frame = document.createElement('iframe');
      frame.style.cssText = VISIBLE_STYLE;
      frame.setAttribute('allow', 'payment');
      frame.src = challengeUrl;
      host.appendChild(frame);
    },
  });
  return res.payload?.transaction as BuyResult;
}
